<template>
  <div class="flex flex-col gap-3 min-h-[200px]">
    <div class="flex items-center gap-2">
      <div class="text-sm text-text-subtle flex-1">
        <template v-if="taskId">Results from <span class="text-text-default">{{ task_label }}</span>.</template>
        <template v-else>{{ $t('scheduled.inbox.intro') }}</template>
      </div>
      <span
        v-if="unread_count"
        class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-subtle text-text-link"
      >{{ $t('scheduled.inbox.unreadCount', unread_count) }}</span>
    </div>

    <!-- The task narrowing, shown as a removable pill rather than as another select. It arrives from a
         row's "Open results" rather than from this screen, so it has to be visible and undoable here —
         otherwise a filtered inbox is indistinguishable from a quiet one. -->
    <div v-if="taskId" class="flex items-center gap-2">
      <span class="text-xs px-2 py-1 rounded bg-brand-subtle text-text-link flex items-center gap-1.5">
        {{ task_label }}
        <i class="ri-close-line cursor-pointer" @click="$emit('clear-task')" />
      </span>
      <button class="text-xs text-text-link" @click="$emit('clear-task')">Show all results</button>
    </div>

    <!-- Filters. Every one of these is a query parameter, never a client-side narrowing: filtering a
         page in the browser returns a short page, which reads as the end of the list. -->
    <div class="flex flex-wrap items-center gap-2">
      <!-- Fixed-width wrappers: FusionDropdown fills its container, which in this wrapping row would be the whole row. -->
      <div class="w-36">
        <fusion-dropdown
          v-model="filter_dealer"
          :options="dealer_filter_options"
          sort-order="none"
          compact
          @change="reload"
        />
      </div>

      <div class="w-44">
        <fusion-dropdown
          v-model="filter_type"
          :options="type_filter_options"
          sort-order="none"
          compact
          @change="reload"
        />
      </div>

      <div class="w-44">
        <fusion-dropdown
          v-model="filter_status"
          :options="status_filter_options"
          sort-order="none"
          compact
          @change="reload"
        />
      </div>

      <label class="flex items-center gap-1 text-xs text-text-subtle">
        <input type="checkbox" v-model="filter_unread_only" @change="reload" />
        {{ $t('scheduled.inbox.unreadOnly') }}
      </label>

      <button
        v-if="rows.length"
        class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link ml-auto"
        :disabled="processing || !unread_on_page.length"
        @click="markPageRead"
      >{{ $t('scheduled.inbox.markPageRead', { count: unread_on_page.length }) }}</button>
    </div>

    <div v-if="processing && !rows.length" class="px-2 py-6 text-sm text-text-subtle">{{ $t('scheduled.common.loading') }}</div>

    <div v-else-if="!rows.length" class="px-2 py-8 text-center text-sm text-text-subtle">
      {{ $t('scheduled.inbox.empty') }}
    </div>

    <template v-else>
      <div class="flex flex-col gap-1">
        <div
          v-for="row in rows"
          :key="row.runId"
          class="flex items-start gap-2 px-3 py-2 bg-surface-1 border border-border-separator rounded-md"
          :class="row.readAtUtc ? 'inbox-row-read' : 'inbox-row-unread font-medium'"
        >
          <span
            class="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
            :class="row.readAtUtc ? 'bg-transparent' : 'bg-brand-outline'"
          ></span>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-2 text-text-subtle flex-shrink-0">
                {{ row.dealerId || '—' }}
              </span>
              <span class="text-sm text-text-default truncate">{{ taskName(row.taskId) }}</span>
              <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-subtle text-text-link flex-shrink-0">
                {{ triggerLabel(row) }}
              </span>
            </div>

            <div class="flex items-center gap-2 text-xs mt-0.5 flex-wrap">
              <span :class="statusClass(row.status)">{{ statusLabel(row) }}</span>
              <span class="text-text-subtle">{{ formatWhen(row.ranAt) }}</span>
              <span v-if="row.detail" class="text-text-subtle truncate">· {{ row.detail }}</span>
            </div>

            <!-- Shown only when DeliveryReachPolicy says there is news. An expected non-delivery on a
                 file or console sink is not news, and warning on it would warn on every single run. -->
            <div v-if="row.hasDeliveryProblem" class="text-xs text-danger mt-0.5 truncate">
              {{ row.deliveryDetail || $t('scheduled.inbox.deliveryProblem') }}
            </div>
          </div>

          <!-- No thread means the run delivered nothing. Saying so beats offering a link that goes
               nowhere — and old runs read back no thread at all, since the field postdates them. -->
          <button
            v-if="row.deliveredThreadId"
            class="text-xs px-2 py-1 rounded outline outline-1 outline-brand-outline text-text-link flex-shrink-0"
            @click="openRow(row)"
          >{{ $t('scheduled.inbox.open') }}</button>
          <span v-else class="text-xs text-text-subtle flex-shrink-0 px-2 py-1">{{ $t('scheduled.inbox.nothingDelivered') }}</span>
        </div>
      </div>

      <button
        v-if="next_cursor"
        class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link self-start"
        :disabled="processing"
        @click="loadMore"
      >{{ $t('scheduled.inbox.loadMore') }}</button>
    </template>

    <div v-if="row_message" class="text-xs text-text-subtle px-1">{{ row_message }}</div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import FusionDropdown from '@components/UI/FusionDropdown.vue';
import { getActiveLocaleTag } from '../../i18n/index.js';

export default {
  name: 'ScheduledInbox',
  components: {
    'fusion-dropdown': FusionDropdown,
  },
  props: {
    /**
     * Narrow to one task, or '' for every task the caller owns. Set by the task list's per-row
     * "Open results"; sent to the server as a query parameter like every other filter here, for the
     * reason stated above the filter row — a page narrowed in the browser is a short page, and a short
     * page reads as the end of the list.
     */
    taskId: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The filtered task's name, for the pill. Falls back to the id, which is what the rows already do.
     * NOT `taskName`: this component already has a taskName(taskId) METHOD that every row calls, and in
     * the Options API a prop of that name silently wins the namespace — the rows would then be calling
     * a string.
     */
    filterTaskName: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['count', 'clear-task'],
  data() {
    return {
      rows: [],
      tasks: [],
      filter_dealer: '',
      filter_type: '',
      filter_status: '',
      filter_unread_only: false,
      next_cursor: null,
      unread_count: 0,
      processing: false,
      row_message: '',
    };
  },
  mounted() {
    this.loadTasks();
    this.reload();
  },
  computed: {
    ...mapStores(useMainStore),
    // The store list comes from the caller's own tasks rather than a lookup: those ARE the stores whose
    // results can appear here, and the server refuses any other one with a 403.
    stores() {
      const seen = this.tasks.map(t => t.dealerId).filter(Boolean);
      return [...new Set(seen)].sort();
    },
    dealer_filter_options() {
      return [{ value: '', label: this.$t('scheduled.inbox.allStores') }, ...this.stores.map(s => ({ value: s, label: s }))];
    },
    type_filter_options() {
      return [
        { value: '', label: this.$t('scheduled.inbox.allTypes') },
        { value: 'ScheduledQuestion', label: this.$t('scheduled.inbox.typeQuestion') },
        { value: 'Watcher', label: this.$t('scheduled.common.taskType.watcher') },
        { value: 'MorningBrief', label: this.$t('scheduled.common.taskType.morningBrief') },
      ];
    },
    status_filter_options() {
      return [
        { value: '', label: this.$t('scheduled.inbox.anyOutcome') },
        { value: 'Delivered', label: this.$t('scheduled.inbox.statusDelivered') },
        { value: 'NothingToReport', label: this.$t('scheduled.inbox.statusNothingToReport') },
        { value: 'Skipped', label: this.$t('scheduled.inbox.statusSkipped') },
        { value: 'Failed', label: this.$t('scheduled.inbox.statusFailed') },
      ];
    },
    unread_on_page() {
      return this.rows.filter(r => !r.readAtUtc);
    },
    // The name the caller passed, else the one in the loaded list, else the bare id. Same fallback
    // ladder the rows use, and for the same reason: a deleted task's runs still exist and still need
    // to say which task they came from.
    task_label() {
      if (this.filterTaskName) return this.filterTaskName;
      const task = this.tasks.find(t => t.id === this.taskId);
      return task?.name || this.taskId;
    },
  },
  methods: {
    loadTasks() {
      const self = this;
      this.mainStore.GetScheduledTasks().then((response) => {
        self.tasks = Array.isArray(response) ? response : [];
      }, () => {
        // The names are decorative; a failure here must not empty the inbox. Rows fall back to the task id.
        self.tasks = [];
      });
    },
    reload() {
      this.next_cursor = null;
      this.rows = [];
      this.load();
    },
    loadMore() {
      this.load(this.next_cursor);
    },
    load(cursor = null) {
      const self = this;
      this.processing = true;
      this.mainStore.GetScheduledInbox(this.requestParams(cursor)).then((response) => {
        const items = Array.isArray(response?.items) ? response.items : [];
        // Appended, not replaced: a "load more" that replaced the page would silently lose everything
        // above it while looking like it worked.
        self.rows = cursor ? self.rows.concat(items) : items;
        self.next_cursor = response?.nextCursor || null;
        // Null means unchanged — the server counts on the first page only, because the badge does not move
        // while you scroll. Blanking it here would make the badge vanish on the second page.
        if (response && response.unreadCount !== null && response.unreadCount !== undefined) {
          self.unread_count = response.unreadCount;
        }
        self.processing = false;
        self.$emit('count', self.unread_count);
      }, () => {
        self.processing = false;
        self.flashMessage(self.$t('scheduled.inbox.loadFailed'));
      });
    },
    requestParams(cursor) {
      const params = { sinceDays: 30, limit: 50 };
      if (this.taskId) params.taskId = this.taskId;
      if (this.filter_dealer) params.dealerId = this.filter_dealer;
      if (this.filter_type) params.taskType = this.filter_type;
      if (this.filter_status) params.status = this.filter_status;
      if (this.filter_unread_only) params.unreadOnly = true;
      if (cursor) params.cursor = cursor;
      return params;
    },
    openRow(row) {
      // Marked read locally first so the row stops being bold immediately; the request is fire-and-forget
      // because a failed stamp is worth far less than a delayed navigation.
      this.markLocally(row);
      this.mainStore.MarkScheduledRunRead(row.taskId, row.runId).catch(() => {});
      // A delivered thread is a Rey conversation — the Scheduled tasks thread, or a brief — so this goes
      // to OptionsMenu, which owns the dialog this panel sits in and closes it on the way into Rey. It
      // used to be MESSENGER-OPEN-THREAD, which only the Internal Messaging window acts on: from the
      // Rey panel nothing opened at all, and in the toolbar the result opened on the hidden tab.
      if (window.event_bus) window.event_bus.$emit('SCHEDULED-OPEN-RESULT-THREAD', row.deliveredThreadId);
    },
    markPageRead() {
      if (this.processing) return;
      const self = this;
      const pending = this.unread_on_page.map(r => ({ taskId: r.taskId, runId: r.runId }));
      if (!pending.length) return;

      this.processing = true;
      this.mainStore.MarkScheduledInboxRead(pending).then(() => {
        self.unread_on_page.slice().forEach(r => self.markLocally(r));
        self.processing = false;
        self.$emit('count', self.unread_count);
      }, () => {
        self.processing = false;
        self.flashMessage(self.$t('scheduled.inbox.markReadFailed'));
      });
    },
    markLocally(row) {
      if (row.readAtUtc) return;
      row.readAtUtc = new Date().toISOString();
      if (this.unread_count > 0) this.unread_count -= 1;
    },
    taskName(taskId) {
      const task = this.tasks.find(t => t.id === taskId);
      // The bare id when the task has been deleted: the run still happened, and hiding the row would be
      // worse than showing one with an unfriendly name.
      return task?.name || taskId;
    },
    triggerLabel(row) {
      // Three answers to "what caused this run": an ingested event, a person pressing run-now (NPM-181),
      // or — null, the historical default — the cadence.
      if (row.triggerSource === 'Event') return this.$t('scheduled.inbox.triggerEvent');
      if (row.triggerSource === 'Manual') return 'Manual';
      return this.$t('scheduled.inbox.triggerCadence');
    },
    // Known statuses map to localized labels; an unrecognized value falls back to the raw server string
    // so a new enum member still shows up rather than blanking the row. The same applies to skipReason.
    statusLabel(row) {
      const statusKeys = {
        Delivered: 'scheduled.inbox.statusDelivered',
        NothingToReport: 'scheduled.inbox.statusNothingToReport',
        Skipped: 'scheduled.inbox.statusSkipped',
        Failed: 'scheduled.inbox.statusFailed',
      };
      const skipReasonKeys = {
        MissedGraceWindow: 'scheduled.inbox.skipReason.missedGraceWindow',
        StoreNotEnabled: 'scheduled.inbox.skipReason.storeNotEnabled',
        NoRooftop: 'scheduled.inbox.skipReason.noRooftop',
        QuietHoursDeferred: 'scheduled.inbox.skipReason.quietHoursDeferred',
        DailyCapReached: 'scheduled.inbox.skipReason.dailyCapReached',
        WatcherEvaluatorUnavailable: 'scheduled.inbox.skipReason.watcherEvaluatorUnavailable',
        BriefFanInUnavailable: 'scheduled.inbox.skipReason.briefFanInUnavailable',
        Cancelled: 'scheduled.inbox.skipReason.cancelled',
        ComputeBudgetExceeded: 'scheduled.inbox.skipReason.computeBudgetExceeded',
        DuplicateSuppressed: 'scheduled.inbox.skipReason.duplicateSuppressed',
        StoreHoursUnknown: 'scheduled.inbox.skipReason.storeHoursUnknown',
        DeliveryFailed: 'scheduled.inbox.skipReason.deliveryFailed',
        StoreClosed: 'scheduled.inbox.skipReason.storeClosed',
        WatcherRowUnavailable: 'scheduled.inbox.skipReason.watcherRowUnavailable',
      };
      // Skipped alone carries every declining meaning at once, so the reason is what makes the row useful.
      if (row.status === 'Skipped' && row.skipReason && row.skipReason !== 'None') {
        return skipReasonKeys[row.skipReason] ? this.$t(skipReasonKeys[row.skipReason]) : row.skipReason;
      }
      return statusKeys[row.status] ? this.$t(statusKeys[row.status]) : row.status;
    },
    statusClass(status) {
      if (status === 'Failed') return 'text-danger';
      if (status === 'Delivered') return 'text-text-link';
      return 'text-text-subtle';
    },
    formatWhen(value) {
      if (!value) return '';
      const at = new Date(value);
      return Number.isNaN(at.getTime()) ? '' : at.toLocaleString(getActiveLocaleTag());
    },
    flashMessage(message) {
      this.row_message = message;
      window.setTimeout(() => { this.row_message = ''; }, 4000);
    },
  },
  watch: {
    // Re-queried rather than re-filtered. The narrowing is a server predicate, so changing it has to go
    // back to the server — and reload() resets the cursor, which a bare re-render would not, leaving
    // page two of the old filter appended to page one of the new one.
    taskId() {
      this.reload();
    },
  },
};
</script>
